"use client";
import React, { useRef, useState, useEffect } from "react";
import { CONTACT_US_DATA } from "@/lib/ConstData";

const sections = [
  {
    title: "A general guide for use",
    content: `Terms of Use govern your use of this website and other services provided by ${CONTACT_US_DATA.companyName} (the "Services"). These Terms of Use, together with our Privacy Policy, and Disclaimer and any other terms specifically referred to in any of those documents, constitute legally binding agreement (the "Agreement") between You and ${CONTACT_US_DATA.companyName} in relation to your use of this website and services (together, the "Platform"). By accepting the terms and conditions you will be legally bound to follow all the below mentioned terms and conditions during your relationship with ${CONTACT_US_DATA.companyName}. Without your acceptance of the terms and conditions, ${CONTACT_US_DATA.companyName} shall not be able to give on Hire any bike. If you disagree with any of the terms of this agreement, please refrain from hiring the bike.

Defined Terms

  "${CONTACT_US_DATA.companyName}" (hereinafter referred as the "Company") is a registered limited liability partnership company in the state of Delhi, India. The registered address of the Company is ${CONTACT_US_DATA.address}.

"Conditions" mean these General Conditions of Use, to carry on the business of letting on hire or otherwise, motorcycle and other vehicles of every kind and description, on such terms and conditions as may be decided by the Company from time to time.

"Service" refers to any service provided by the Company, as the Company deems fit, to any User.

"User" means a person who has accepted the terms and conditions for leasing or renting a motorcycle or motor vehicle of any kind from the Company.

"Vehicle" means the motorcycle/motorbike/scooter or motor vehicle of any kind offered by the Company to any User for leasing/renting for the period as determined between the Company and the User.

These Conditions are intended to create binding rights and obligations between the User and the Company in accordance with the Indian Contract Act, 1872. The Company reserves the right to change the terms of these Conditions from time to time. No access to the Services will be permitted unless the Conditions are accepted in full. No User is entitled to accept only part of the Conditions. If a User does not agree to all the Conditions, such User may not use the Services. In the event that any Member fails to comply with any of the Conditions, the Company reserves the right, at its own discretion, to suspend or withdraw all Services to that User without notice.`
  },
  {
    title: "Use of the vehicle",
    content: `To avail certain services on our Websites, users are required to provide certain information for the registration process namely:

Name
Email address
Sex
Age
PIN code
Credit card or Debit card details
Medical records and history
Sexual orientation
Biometric information
Password
Occupation, interests, and the like

The Information as supplied by the users enables us to improve our sites and provide you the most user-friendly experience. All required information is service dependent and we may use the above said user information to, maintain, protect, and improve its services (including advertising services) and for developing new services Such information will not be considered as sensitive if it is freely available and accessible in the public domain or is furnished under the Right to Information Act, 2005 or any other law for the time being in force`
  },
  {
    title: "Prohibited Uses",
    content: "The vehicle must not be used for illegal activities, racing, or any activity that violates local laws or our policies. Users are strictly prohibited from using the vehicle for any unlawful purpose, including but not limited to smuggling, transporting illegal goods, or engaging in activities that could harm public safety."
  },
  {
    title: "Maintenance/Vehicle Condition/Return/Stolen Vehicles",
    content: "You must report any maintenance issues or theft immediately to our support team. Failure to do so may result in liability for damages. Regular maintenance checks are required, and any damage must be reported within 24 hours of discovery."
  },
  {
    title: "Trip Extension",
    content: "If you wish to extend your trip, please do so through the app or website before your current booking expires. Extensions are subject to availability and may incur additional charges based on current rates."
  },
  {
    title: "Fee Policy",
    content: "All fees are clearly stated at the time of booking. Additional charges may apply for late returns, damages, or violations of terms. Payment must be made in full before the vehicle is released for use."
  },
  {
    title: "Refund Policy",
    content: "The refunded amount will reflect in the customer's bank account within 7-10 business days."
  },
  {
    title: "No Agency",
    content: "Nothing in these terms creates an agency, partnership, or joint venture between you and the company. You are an independent user of our services and not an employee or representative of the company."
  },
  {
    title: "Offers and Promotions",
    content: "Offers and promotions are subject to change without notice and may have specific terms and conditions. We reserve the right to modify or discontinue any promotional offers at any time."
  },
  {
    title: "Traffic Law/Applicable Law Violations",
    content: "You are responsible for any traffic or legal violations incurred during your booking period. This includes parking tickets, speeding violations, and any other legal infractions that occur while using our vehicle."
  },
  {
    title: "Insurance/Liability",
    content: "Insurance coverage is provided as per the policy details shared at the time of booking. You may be liable for damages not covered by insurance. It is your responsibility to understand the extent of coverage provided."
  },
  {
    title: "Acceptable Website Use",
    content: "You agree to use the website in accordance with all applicable laws and not to engage in any activity that disrupts or interferes with the site. This includes but is not limited to hacking, spamming, or attempting to gain unauthorized access to our systems."
  },
  {
    title: "Disclaimer of Consequential Damages",
    content: "The company is not liable for any indirect, incidental, or consequential damages arising from the use of our services. This includes lost profits, data loss, or any other damages not directly caused by our negligence."
  },
  {
    title: "Intellectual Property Rights",
    content: "All content, trademarks, and intellectual property on the website are owned by the company or its licensors. You may not reproduce, distribute, or create derivative works without our express written permission."
  },
  {
    title: "Dispute Resolution/Arbitration",
    content: "Any disputes arising from these terms will be resolved through arbitration as per the applicable laws. Both parties agree to submit to binding arbitration in the event of any disagreement."
  },
  {
    title: "Governing Law and Jurisdiction",
    content: "These terms are governed by the laws of India. Any disputes will be subject to the jurisdiction of courts in India. All legal proceedings must be conducted in accordance with Indian law."
  },
  {
    title: "Entire Agreement",
    content: "These terms constitute the entire agreement between you and the company regarding the use of our services. Any previous agreements or understandings are superseded by these terms."
  }
];

export default function TermsClient() {
  const sectionRefs = useRef(sections.map(() => React.createRef<HTMLDivElement>()));
  const [activeSection, setActiveSection] = useState(0);

  const handleChipClick = (idx: number) => {
    setActiveSection(idx);
    const element = sectionRefs.current[idx]?.current;
    if (element) {
      const offset = 120; // Offset for header and padding
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth"
      });
    }
  };

  // Simple scroll listener to track which section is currently visible
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for better detection
      
      sectionRefs.current.forEach((ref, index) => {
        if (ref.current) {
          const elementTop = ref.current.offsetTop;
          const elementBottom = elementTop + ref.current.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(index);
          }
        }
      });
    };

    // Initial check
    handleScroll();
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <main className="w-full min-h-screen bg-white">
      {/* Mobile Tab View - Fixed */}
      <div className="md:hidden fixed top-20 left-0 right-0 z-50 bg-white border-b border-gray-200 ">
        <div className="pt-6 pb-4">
          <h1 className="text-xl md:text-2xl font-semibold text-black leading-[1.22] tracking-[-0.01em] mb-6 px-4">
            Terms & Conditions
          </h1>
          <div className="flex overflow-x-auto gap-2 pb-2 hide-scrollbar w-full px-4">
            {sections.map((section, idx) => (
              <button
                key={idx}
                onClick={() => handleChipClick(idx)}
                className={`whitespace-nowrap px-3 py-2.5 text-xs sm:text-sm font-medium rounded-full border transition-all duration-200 flex-shrink-0 min-w-fit shadow-sm ${
                  activeSection === idx 
                    ? 'bg-[#F05A24] text-white border-[#F05A24] shadow-md' 
                    : 'bg-white text-black border-gray-200 hover:border-[#F05A24] hover:text-[#F05A24] hover:shadow-md'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:gap-12 pt-[150px] md:pt-0">
        {/* Desktop Table of Contents Sidebar */}
        <div className="hidden md:block md:w-[370px] md:flex-shrink-0">
          <div className="sticky top-8 bg-white rounded-[32px] shadow-[0px_20px_48px_0px_rgba(0,0,0,0.12)] pb-8">
            <div className="flex items-center gap-2 px-4 py-4 my-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-black">Contents</h2>
            </div>
            <div className="flex flex-col gap-4 px-4">
              {sections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => handleChipClick(idx)}
                  className={`text-left text-base leading-[1.19] transition-colors ${
                    activeSection === idx 
                      ? 'text-[#F05A24] font-medium' 
                      : 'text-black font-normal hover:text-[#F05A24]'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Desktop Title */}
          <div className="hidden md:block mb-12">
            <h1 className="text-[48px] font-medium text-black leading-[1.22] tracking-[-0.01em]">
              Terms & Conditions
            </h1>
          </div>

          <div className="flex flex-col gap-6 sm:gap-8 md:gap-12">
            {sections.map((section, idx) => (
              <div
                key={idx}
                ref={sectionRefs.current[idx]}
                className="bg-[#F4F4F4] rounded-[16px] sm:rounded-[20px] md:rounded-[24px] p-6 sm:p-8"
              >
                <h2 className="text-lg sm:text-xl font-medium text-black mb-4 sm:mb-6">
                  {section.title}
                </h2>
                <div className="text-sm sm:text-base leading-[1.19] tracking-[-0.01em] text-black/50 whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
